import {
  Component,
  MapCacheContext,
  mergeObjectDeep,
  Props,
  scheduleIdle,
  type ComponentContainer,
  type VNode
} from "@zyzgroup/core-common";
import {
  atomReadAttributes,
  atomWriteAttribute,
  atomRemoveStyle,
  atomWriteStyle
} from "../dom";
import { createSVGElement, createSvgPath } from "../svg";

// h("div", { style: "margin:10px; border:1px solid #ccc; padding:10px;" }, [
//   h("h2", {}, `Count: ${count}`),
//   h("button", { onclick: () => setCount((c) => c + 1) }, "Increment"),
//   h(
//     "button",
//     { onclick: () => setShowTimer((s) => !s) },
//     showTimer ? "Hide Timer" : "Show Timer"
//   ),
//   showTimer && h(Timer, { start: 0 }) // 嵌套 Timer 组件
// ]);
// 将 tag / Component 转化为 vnode
export function h(
  tag: string | Component,
  props: Props = {},
  children: (VNode | boolean)[] | string = [] // 支持条件渲染
): VNode {
  if (typeof tag == "string") {
    // HTML原生元素
    return {
      tag,
      props,
      children:
        typeof children == "string"
          ? children
          : children.filter((child) => typeof child != "boolean")
    };
  } else {
    // Component
    return tag(props);
  }
}

// 前提：el已挂载到dom上！
export function hFromEl(el: ComponentContainer): VNode {
  const vnode = h(
    el.tagName,
    atomReadAttributes(el),
    Array.from(el.children).map((child) => hFromEl(child as ComponentContainer))
  );
  vnode.el = el;
  return vnode;
}

// 将 vnode 挂载到 真实dom 上
export function mountVNode(
  vnode: VNode | string,
  parent: ComponentContainer = document.documentElement
): ComponentContainer {
  if (typeof vnode === "string") {
    const el = document.createTextNode(vnode);
    parent.appendChild(el);
    // parent.textContent = String(vnode);
    // parent.innerHTML = String(vnode);
    return el as unknown as ComponentContainer;
  }
  if (vnode.el) {
    return vnode.el;
  }
  // HTML原生元素
  const el =
    vnode.tag == "path" ? createSvgPath() : document.createElement(vnode.tag);
  // const el = document.createDocumentFragment() as unknown as ComponentContainer;
  // 添加属性
  if (vnode.props) {
    for (const [k, v] of Object.entries(vnode.props)) {
      if (k.startsWith("on")) {
        el.addEventListener(k.slice(2).toLowerCase(), v);
      } else if (k === "style") {
        atomWriteStyle(el, v);
      } else {
        atomWriteAttribute(el, k, v);
      }
    }
  }
  parent.appendChild(el);
  vnode.el = el;
  // 递归
  if (vnode.children) {
    if (typeof vnode.children == "string") {
      mountVNode(vnode.children, el);
    } else {
      vnode.children.forEach((child) => {
        mountVNode(child, el);
      });
    }
  }
  return el;
}

export function unmountVNode(
  vnode: VNode | string,
  parent: ComponentContainer
): void {
  if (typeof vnode === "string" || !vnode.el) {
    return;
  }
  if (Array.isArray(vnode.children)) {
    vnode.children.forEach((child) => {
      if (!vnode.el) {
        return;
      }
      unmountVNode(child, vnode.el);
    });
  }
  parent.removeChild(vnode.el);
}

// diff vnode to 真实dom
export function patchVNode(
  oldVNode: VNode,
  newVNode: VNode,
  parent?: ComponentContainer
): void {
  if (!oldVNode.el || !newVNode.el) {
    return;
  }
  parent =
    parent ||
    oldVNode.el.parentElement ||
    document.body ||
    document.documentElement;
  const oldKey = oldVNode.props.key;
  const newKey = newVNode.props.key;
  // 不同tag 或 不同el 或 不同key(key列表优化)
  if (
    oldVNode.tag !== newVNode.tag ||
    oldVNode.el !== newVNode.el ||
    ((newKey || oldKey) && newKey !== oldKey)
  ) {
    // 替换节点
    // unmountVNode(oldVNode, parent);
    // mountVNode(newVNode, parent);
    parent.replaceChild(mountVNode(newVNode, parent), oldVNode.el);
    return;
  }
  // 相同
  const el = (newVNode.el = oldVNode.el);
  const oldProps = oldVNode.props;
  const newProps = newVNode.props;
  // 删除旧属性
  for (const k in oldProps) {
    if (!(k in newProps)) {
      if (k.startsWith("on")) {
        el.removeEventListener(k.slice(2).toLowerCase(), oldProps[k]);
      } else {
        el.removeAttribute(k);
      }
    }
  }
  // 新增新属性
  for (const k in newProps) {
    const oldValue = oldProps[k];
    const newValue = newProps[k];
    if (newValue != oldValue) {
      if (k.startsWith("on")) {
        const name = k.slice(2).toLowerCase();
        el.removeEventListener(name, oldValue);
        el.addEventListener(name, newValue);
      } else if (k === "style") {
        for (const sk in oldValue) {
          if (!(sk in newValue)) {
            atomRemoveStyle(el, sk);
          }
        }
        atomWriteStyle(el, newValue);
      } else {
        atomWriteAttribute(el, k, newValue);
      }
    }
  }
  // 递归
  const oldChildren = oldVNode.children || [];
  const newChildren = newVNode.children || [];
  if (typeof newChildren == "string") {
    el.innerHTML = "";
    mountVNode(newChildren, el);
  } else {
    if (typeof oldChildren == "string") {
      el.innerHTML = "";
      newChildren.forEach((item) => {
        mountVNode(item, el);
      });
    } else {
      const commonMinLength = Math.min(oldChildren.length, newChildren.length);
      for (let i = 0; i < commonMinLength; i++) {
        patchVNode(oldChildren[i], newChildren[i], el);
      }
      if (newChildren.length > oldChildren.length) {
        newChildren.slice(oldChildren.length).forEach((item) => {
          mountVNode(item, el);
        });
      } else if (newChildren.length < oldChildren.length) {
        oldChildren.slice(newChildren.length).forEach((item) => {
          unmountVNode(item, el);
        });
      }
    }
  }
}

// 渲染
export function renderVNode(
  parent: ComponentContainer = document.documentElement,
  newVNode: VNode,
  oldVNode?: VNode
): void {
  if (!newVNode.el) {
    if (oldVNode) {
      unmountVNode(oldVNode, parent);
    }
    mountVNode(newVNode, parent);
  } else if (!oldVNode || !oldVNode.el) {
    unmountVNode(newVNode, parent);
    mountVNode(newVNode, parent);
  } else {
    patchVNode(oldVNode, newVNode, parent);
  }
}

/////////////////////////////////////////////////////////////////

const globalVNodeCache = new Map();

// 前提：el已挂载到dom上！
export function getVNode(
  el: ComponentContainer,
  cache: MapCacheContext = globalVNodeCache
): VNode {
  // 性能瓶颈：如何从 el 快速获得唯一id ?
  let vnode = cache.get(el);
  if (vnode) {
    return vnode;
  }
  vnode = hFromEl(el);
  cache.set(el, vnode);
  return vnode;
}

// 前提：el已挂载到dom上！
export function updateVNode(
  el: ComponentContainer,
  update: (vnode: VNode) => void,
  cache: MapCacheContext = globalVNodeCache
) {
  const vnode = getVNode(el, cache);
  if (!vnode.alternate) {
    vnode.alternate = hFromEl(el);
    mergeObjectDeep(vnode.alternate.props, vnode.props, true);
  }
  update(vnode.alternate);
}

// 更新 vnode to 真实dom
// 前提：el已挂载到dom上！
export function scheduleVNodeImmediately(cache = globalVNodeCache) {
  const entries = Array.from(cache.entries());
  for (const [el, vnode] of entries) {
    const alternate = vnode.alternate;
    if (alternate) {
      patchVNode(vnode, alternate);
      // 提高性能：不改变 cache 中 vnode 引用
      vnode.props = alternate.props;
      vnode.alternate = undefined;
    }
  }
}

export function scheduleVNodeAsap(cache = globalVNodeCache) {
  scheduleIdle(() => scheduleVNodeImmediately(cache), 50);
}
